import type { Editor } from "grapesjs";
import type { BuilderTheme } from "@/lib/builder-theme";
import {
  buildLayoutFooterHtml,
  buildLayoutHeaderHtml,
} from "@/lib/layout-template";
import type { MenuItem } from "@/lib/navigation";

export type LayoutPluginOptions = {
  theme: BuilderTheme;
  menuItems: MenuItem[];
  page: string;
};

export type LayoutRegions = {
  headerHtml: string;
  contentHtml: string;
  footerHtml: string;
};

const HEADER_TYPE = "rt-layout-header";
const FOOTER_TYPE = "rt-layout-footer";
const MENU_TYPE = "rt-layout-menu";
const CONTENT_TYPE = "rt-layout-content";
const SECTION_TYPE = "rt-layout-section";
const ROW_TYPE = "rt-layout-row";
const COLUMN_TYPE = "rt-layout-column";

export function registerLayoutPlugin(editor: Editor, options: LayoutPluginOptions) {
  registerLayoutComponents(editor);
  registerContentStructureBlocks(editor);
}

export function buildEditorLayoutHtml({
  theme,
  menuItems,
  page,
  contentHtml,
}: LayoutPluginOptions & { contentHtml: string }) {
  return [
    buildLayoutHeaderHtml({ theme, menuItems, page }),
    `<main data-builder-region="content">${contentHtml}</main>`,
    buildLayoutFooterHtml({ theme, page }),
  ].filter(Boolean).join("\n");
}

export function extractLayoutRegions(html: string): LayoutRegions {
  if (typeof DOMParser === "undefined") {
    return {
      headerHtml: "",
      contentHtml: html,
      footerHtml: "",
    };
  }

  const doc = new DOMParser().parseFromString(`<div data-rt-root>${html}</div>`, "text/html");
  const root = doc.querySelector("[data-rt-root]");
  if (!root) {
    return {
      headerHtml: "",
      contentHtml: html,
      footerHtml: "",
    };
  }

  const header = root.querySelector("header.topbar, [data-builder-region='header']");
  const footer = root.querySelector("footer.footer, [data-builder-region='footer']");
  const content = root.querySelector("main[data-builder-region='content']");

  return {
    headerHtml: header ? cleanLayoutHtml(header.outerHTML) : "",
    contentHtml: content ? content.innerHTML.trim() : withoutLayoutRegions(root).trim(),
    footerHtml: footer ? cleanLayoutHtml(footer.outerHTML) : "",
  };
}

export function stripLayoutCss(css: string) {
  return css
    .replace(/[^{}]*(?:\.topbar\b|\.nav\b|\.brand\b|\.nav-links\b|\.nav-link\b|\.admin-entry\b|\.footer(?:\b|-)|#izk6xak\b|\[data-builder-region=["']?(?:header|footer)["']?\])[^{}]*\{[^{}]*\}/gi, "")
    .replace(/[^{}]*\.base-media-row[^{}]*\{[^{}]*\}/gi, "")
    .replace(/@media[^{]*\{\s*\}/gi, "")
    .trim();
}

export function scopeRenderedPageCss(css: string) {
  return css.replace(/(^|})([^@{}][^{}]*)\{([^{}]*)\}/g, (match, boundary: string, selectorText: string, declarations: string) => {
    const scopedSelectors = selectorText
      .split(",")
      .map((selector) => selector.trim())
      .filter(Boolean)
      .map((selector) => {
        if (selector.startsWith(".grapes-rendered-page")) return selector;
        if (selector === "body" || selector === "html" || selector === ":root") return ".grapes-rendered-page";
        return `.grapes-rendered-page ${selector}`;
      })
      .join(", ");

    return scopedSelectors ? `${boundary}${scopedSelectors}{${declarations}}` : match;
  });
}

function withoutLayoutRegions(root: Element) {
  const clone = root.cloneNode(true) as Element;
  clone.querySelectorAll("header.topbar, footer.footer, [data-builder-region='header'], [data-builder-region='footer']").forEach((node) => node.remove());
  const main = clone.querySelector("main[data-builder-region='content']");
  return main ? main.innerHTML : clone.innerHTML;
}

function cleanLayoutHtml(html: string) {
  return html
    .replace(/\sdata-gjs-[a-z0-9-]+="[^"]*"/gi, "")
    .replace(/\sdata-highlightable="[^"]*"/gi, "")
    .replace(/\sdraggable="[^"]*"/gi, "")
    .trim();
}

function registerLayoutComponents(editor: Editor) {
  const domc = editor.DomComponents;

  if (!domc.getType(CONTENT_TYPE)) {
    domc.addType(CONTENT_TYPE, {
      isComponent: (el) => {
        if (el instanceof HTMLElement && el.matches("main[data-builder-region='content']")) {
          return { type: CONTENT_TYPE };
        }
        return false;
      },
      model: {
        defaults: {
          name: "页面内容区",
          draggable: false,
          droppable: "section",
          traits: [
            { type: "text", name: "data-builder-region", label: "区域" },
            { type: "text", name: "class", label: "样式类" },
          ],
        },
      },
    });
  }

  if (!domc.getType(SECTION_TYPE)) {
    domc.addType(SECTION_TYPE, {
      isComponent: (el) => {
        if (el instanceof HTMLElement && el.matches("section")) {
          return { type: SECTION_TYPE };
        }
        return false;
      },
      model: {
        defaults: {
          name: "Section 区段",
          draggable: "main[data-builder-region='content']",
          droppable: "[data-rt-layout='row'], .rt-layout-row",
          traits: [
            { type: "text", name: "class", label: "样式类" },
            { type: "text", name: "data-rt-section", label: "区段标识" },
          ],
        },
      },
    });
  }

  if (!domc.getType(ROW_TYPE)) {
    domc.addType(ROW_TYPE, {
      isComponent: (el) => {
        if (el instanceof HTMLElement && el.matches("[data-rt-layout='row'], .rt-layout-row")) {
          return { type: ROW_TYPE };
        }
        return false;
      },
      model: {
        defaults: {
          name: "Row 行",
          draggable: "section",
          droppable: "[data-rt-layout='column'], .rt-layout-column",
          traits: [
            { type: "text", name: "class", label: "样式类" },
            {
              type: "select",
              name: "data-rt-columns",
              label: "列数",
              options: [
                { id: "1", name: "1 列" },
                { id: "2", name: "2 列" },
                { id: "3", name: "3 列" },
                { id: "4", name: "4 列" },
              ],
            },
          ],
        },
      },
    });
  }

  if (!domc.getType(COLUMN_TYPE)) {
    domc.addType(COLUMN_TYPE, {
      isComponent: (el) => {
        if (el instanceof HTMLElement && el.matches("[data-rt-layout='column'], .rt-layout-column")) {
          return { type: COLUMN_TYPE };
        }
        return false;
      },
      model: {
        defaults: {
          name: "Column 列",
          draggable: "[data-rt-layout='row'], .rt-layout-row",
          droppable: true,
          traits: [
            { type: "text", name: "class", label: "样式类" },
          ],
        },
      },
    });
  }

  if (!domc.getType(HEADER_TYPE)) {
    domc.addType(HEADER_TYPE, {
      isComponent: (el) => {
        if (el instanceof HTMLElement && el.matches("header.topbar, [data-builder-region='header']")) {
          return { type: HEADER_TYPE };
        }
        return false;
      },
      model: {
        defaults: {
          name: "页眉",
          draggable: false,
          droppable: false,
          copyable: false,
          removable: false,
          traits: [
            { type: "text", name: "data-builder-region", label: "全局区域", readonly: true },
          ],
        },
      },
    });
  }

  if (!domc.getType(FOOTER_TYPE)) {
    domc.addType(FOOTER_TYPE, {
      isComponent: (el) => {
        if (el instanceof HTMLElement && el.matches("footer.footer, [data-builder-region='footer']")) {
          return { type: FOOTER_TYPE };
        }
        return false;
      },
      model: {
        defaults: {
          name: "页脚",
          draggable: false,
          droppable: false,
          copyable: false,
          removable: false,
          traits: [
            { type: "text", name: "data-builder-region", label: "全局区域", readonly: true },
          ],
        },
      },
    });
  }

  if (!domc.getType(MENU_TYPE)) {
    domc.addType(MENU_TYPE, {
      isComponent: (el) => {
        if (el instanceof HTMLElement && el.matches("nav.nav-links, .nav-dropdown, [data-rt-menu]")) {
          return { type: MENU_TYPE };
        }
        return false;
      },
      model: {
        defaults: {
          name: "菜单",
          draggable: false,
          droppable: false,
          copyable: false,
          removable: false,
          traits: [
            { type: "text", name: "data-rt-menu", label: "全局菜单", readonly: true },
          ],
        },
      },
    });
  }
}

function registerContentStructureBlocks(editor: Editor) {
  const category = "页面结构";
  const blocks = editor.BlockManager;

  if (!blocks.get("layout-section-blank")) {
    blocks.add("layout-section-blank", {
      label: "Section 区段",
      category,
      media: '<svg viewBox="0 0 80 60"><rect x="10" y="10" width="60" height="40" rx="4" fill="#e8f1f8" stroke="#31587f" stroke-width="3"/><path d="M22 24h36M22 36h24" stroke="#79a6d3" stroke-width="4"/></svg>',
      content: `
        <section class="rt-section rt-layout-section" data-rt-section="custom">
          <div class="rt-layout-row" data-rt-layout="row" data-rt-columns="1">
            <div class="rt-layout-column" data-rt-layout="column">
              <h2>新区段标题</h2>
              <p>在这里编辑文字、图片、按钮或继续拖入内容模块。</p>
            </div>
          </div>
        </section>
      `,
    });
  }

  if (!blocks.get("layout-row-one")) {
    blocks.add("layout-row-one", {
      label: "一列 Row",
      category,
      media: '<svg viewBox="0 0 80 60"><rect x="12" y="14" width="56" height="32" rx="4" fill="#e8f1f8"/><path d="M24 28h32M24 38h22" stroke="#31587f" stroke-width="4"/></svg>',
      content: `
        <div class="rt-layout-row" data-rt-layout="row" data-rt-columns="1">
          <div class="rt-layout-column" data-rt-layout="column">
            <h3>一列内容</h3>
            <p>适合标题、正文、表单或插件区域。</p>
          </div>
        </div>
      `,
    });
  }

  if (!blocks.get("layout-row-two")) {
    blocks.add("layout-row-two", {
      label: "两列 Row",
      category,
      media: '<svg viewBox="0 0 80 60"><rect x="10" y="14" width="27" height="32" rx="3" fill="#e8f1f8"/><rect x="43" y="14" width="27" height="32" rx="3" fill="#d9e7f5"/><path d="M17 28h14M50 28h14" stroke="#31587f" stroke-width="4"/></svg>',
      content: `
        <div class="rt-layout-row" data-rt-layout="row" data-rt-columns="2">
          <div class="rt-layout-column" data-rt-layout="column">
            <h3>左列内容</h3>
            <p>可放文字、按钮或图片。</p>
          </div>
          <div class="rt-layout-column" data-rt-layout="column">
            <h3>右列内容</h3>
            <p>可放产品信息、图片或插件。</p>
          </div>
        </div>
      `,
    });
  }

  if (!blocks.get("layout-row-three")) {
    blocks.add("layout-row-three", {
      label: "三列 Row",
      category,
      media: '<svg viewBox="0 0 80 60"><rect x="8" y="14" width="18" height="32" rx="3" fill="#e8f1f8"/><rect x="31" y="14" width="18" height="32" rx="3" fill="#d9e7f5"/><rect x="54" y="14" width="18" height="32" rx="3" fill="#e8f1f8"/></svg>',
      content: `
        <div class="rt-layout-row" data-rt-layout="row" data-rt-columns="3">
          <div class="rt-layout-column" data-rt-layout="column"><h3>第一列</h3><p>编辑内容。</p></div>
          <div class="rt-layout-column" data-rt-layout="column"><h3>第二列</h3><p>编辑内容。</p></div>
          <div class="rt-layout-column" data-rt-layout="column"><h3>第三列</h3><p>编辑内容。</p></div>
        </div>
      `,
    });
  }
}
